import React from "react";
import {
  Box,
  Typography,
  Grid,
  Stack,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useTheme,
} from "@mui/material";
import { LocationOn, Person, Info, Close } from "@mui/icons-material";

const ActivityDetailDialog = ({ open, onClose, log }) => {
  const theme = useTheme();

  if (!log) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 700,
        }}
      >
        Activity Details
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={4} alignItems="stretch">
            {/* Target Info */}
            <Grid item xs={12} md={4}>
              <Card
                elevation={0}
                sx={{
                  border: "1px solid #e8f5e8",
                  borderRadius: 3,
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" mb={1} fontWeight={600}>
                    Target Information
                  </Typography>
                  <Stack spacing={0}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Target Type
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: 14 }}>
                        {log.target?.type || "N/A"}
                      </Typography>
                    </Box>
                    {/* <Box>
                      <Typography variant="caption" color="text.secondary">
                        Action
                      </Typography>
                      <Typography
                        variant="body2"
                        justifyContent={"center"}
                        sx={{ fontSize: 14 }}
                      >
                        {log.action || "N/A"}
                      </Typography>
                    </Box> */}

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Target ID
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          backgroundColor:
                            theme.palette.mode === "light" ? "#f4f6f8" : "#6d707073",
                          p: 1,
                          borderRadius: 1,
                          display: "inline-block",
                        }}
                      >
                        {log.target?._id || "N/A"}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Old/New Values */}
            {(log.oldValue || log.newValue) && (
              <Grid item xs={12} md={8}>
                <Card
                  sx={{
                    border: "1px solid #f3e5f5",
                    borderRadius: 3,
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" mb={1} fontWeight={600}>
                      Value Changes
                    </Typography>
                    <Grid container spacing={1}>
                      {log.oldValue && (
                        <Grid item xs={12} md={log.newValue ? 6 : 12}>
                          <Typography variant="subtitle2" color="error">
                            Previous Value
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              p: 2,
                              backgroundColor:
                                theme.palette.mode === "light"
                                  ? "#fbe9e7"
                                  : "#6d707073",
                              borderRadius: 2,
                              fontFamily: "monospace",
                              maxHeight: 250,
                              overflow: "auto",
                            }}
                          >
                            {JSON.stringify(log.oldValue, null, 2)}
                          </Box>
                        </Grid>
                      )}
                      {log.newValue && (
                        <Grid item xs={12} md={log.oldValue ? 6 : 12}>
                          <Typography variant="subtitle2" color="success.main">
                            Current Value
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              p: 2,
                              backgroundColor:
                                theme.palette.mode === "light"
                                  ? "#e8f5e9"
                                  : "#6d707073",
                              borderRadius: 2,
                              fontFamily: "monospace",
                              maxHeight: 250,
                              overflow: "auto",
                            }}
                          >
                            {JSON.stringify(log.newValue, null, 2)}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12}>
              <Grid container spacing={2}>
                {/* User Info */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      border: "1px solid #e8eaf6",
                      borderRadius: 3,
                      height: "100%",
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" mb={1} fontWeight={600}>
                        User Information
                      </Typography>
                      <Stack spacing={0}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body2">
                            {log.user?.email}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Name
                          </Typography>
                          <Typography variant="body2">
                            {log.user?.name || "Not Defined"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Role
                          </Typography>
                          <Typography variant="body2">
                            {log.user?.userType === "admin"
                              ? log.user?.roleId?.title
                              : log.user.userType || "N/A"}
                          </Typography>
                        </Box>
                        {log.user?.userType === "customer" && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Brand Id
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                backgroundColor:
                                  theme.palette.mode === "light"
                                    ? "#f4f6f8"
                                    : "#6d707073",
                                p: 1,
                                borderRadius: 1,
                                display: "block",
                              }}
                            >
                              {log.user?.brandId?._id || "N/A"}
                            </Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            User ID
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              backgroundColor:
                                theme.palette.mode === "light"
                                  ? "#f4f6f8"
                                  : "#6d707073",
                              p: 1,
                              borderRadius: 1,
                              display: "block",
                            }}
                          >
                            {log.user?._id}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Metadata */}
                {log.metadata && (
                  <Grid item xs={12} md={8}>
                    <Card
                      sx={{
                        border: "1px solid #e1f5fe",
                        borderRadius: 3,
                        height: "100%",
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" mb={1} fontWeight={600}>
                          Additional Metadata
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            p: 2,
                            backgroundColor:
                              theme.palette.mode === "light"
                                ? "#fafafa"
                                : "#6d707073",
                            borderRadius: 2,
                            fontFamily: "monospace",
                            maxHeight: 300,
                            overflow: "auto",
                          }}
                        >
                          {JSON.stringify(log.metadata, null, 2)}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityDetailDialog;
